/**!
 * Created by Leonardo Cheslacki on 14/02/2024.
 *
 * MIT License
 *
 * Copyright (c) 2024 cheslacki
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.

 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';

import HelpersController from './../HelpersController';

let _this = {};

/**
 *
 */
class GamesGenius extends HelpersController {
    /**
     *
     * @param props
     * @param main
     */
    constructor(props, main) {
        super(props);

        this.props = props;
        this.main = main;

        this.state = {
            round_answers: [],
            player_answers: [],
            difficulty: 4,
            interval_decrease: 0,
            score: 0,
            high_score: 0
        };

        this.$waitingPlayerAnswer = false;
        this.$canStartRound = true;
        this.$objectResponse = {};

        _this = this;
    };

    /**
     *
     * @returns {*}
     */
    get = () => {
        const {current_name} = this.props;

        return this.main.getState(`_this_${current_name}`);
    };

    /**
     *
     * @returns {string}
     */
    occurrence = () => {
        const {currentFrame} = this.get();

        return this.padWithLeadingZeros(currentFrame, 2);
    };

    /**
     *
     * @returns {string|*}
     */
    getFrame = () => {
        return this.verifyField(this.get(), `occ_frame_${this.occurrence()}`);
    };

    /**
     *
     * @param event
     * @returns {`frame${string}`}
     */
    getLabel = (event) => {
        let {name} = event.target;

        return `frame${this.padWithLeadingZeros(name.substring(name.length - 2), 2)}`;
    };

    updateScore = () => {
        const
            _this_current = this.get(),
            {occ_score, occ_high_score} = _this_current;

        occ_score.text = this.state.score.toString();
        occ_high_score.text = this.state.high_score.toString();
    };

    /**
     *
     * @param all
     */
    clear = (all = false) => {
        const
            {name} = this.props,
            {occ_menu_start, btn_start} = this.get();

        this.state.round_answers = [];
        this.state.player_answers = [];
        this.state.difficulty = 4;
        this.state.interval_decrease = 0;
        this.state.score = 0;

        if (all) {
            this.state.high_score = 0;

            this.$waitingPlayerAnswer = false;
            this.$canStartRound = true;
            this.$countDown = null;

            this.clearThrottle(`timer-${name}-enabled`);
            this.clearThrottle(`timer-${name}-disabled`);
            this.clearThrottle(`timer-${name}-process`);
            this.toggleGenius(true);
            btn_start.visible = true;
            occ_menu_start.gotoAndStop('start');
            this.updateScore();
        }
    };

    /**
     * Toggle mouse enabled.
     * @param disabled
     */
    toggleGenius = (disabled = false) => {
        const
            {name, btn_genius} = this.props,
            _this_current = this.get();

        this.recursiveSyncMap(btn_genius, 0, btn_genius.length, (item, next) => {
            let genius = _this_current[`btn_${name}_${item}`];

            if (disabled) {
                genius.mouseEnabled = false;
            } else {
                genius.mouseEnabled = !genius.mouseEnabled;
            }

            next(true);
        });
    };

    /**
     *
     */
    callRound = () => {
        const
            {btn_genius} = this.props,
            {difficulty, round_answers} = this.state,
            {occ_menu_start, btn_start} = this.get(),
            loopLimit = (difficulty - round_answers.length);

        this.state.player_answers = [];

        btn_start.visible = false
        occ_menu_start.gotoAndStop('wait');

        this.recursiveSyncMap(this.createArray(loopLimit), 0, loopLimit, (item, next) => {
            const random_color = this.randomArray(btn_genius);

            this.state.round_answers.push(random_color);

            next(true);
        }, () => {
            this.state.round_answers = this.shuffleArray(this.state.round_answers);

            this.displaySequence();
        });
    };

    /**
     *
     * @param is_correct
     */
    revampDifficulty = (is_correct) => {
        const
            {decrease, decrease_limit} = this.props,
            {interval_decrease} = this.state;

        if (is_correct) {
            this.state.difficulty++;
            this.state.interval_decrease = (interval_decrease < decrease_limit) ? (interval_decrease + decrease) : interval_decrease;
        } else {
            this.clear();
        }
    };

    processAnswers = () => {
        const
            {name, delay_sequence} = this.props,
            {round_answers, player_answers} = this.state,
            _this_current = this.get(),
            {occ_menu_start, btn_start} = _this_current;

        this.$waitingPlayerAnswer = false;

        this.toggleGenius();

        let isCorrect = true;

        this.recursiveSyncMap(round_answers, 0, round_answers.length, (item, next, stop, index) => {
            if (item !== player_answers[index]) {
                isCorrect = false;
                stop(true);
            } else {
                next(true);
            }
        }, () => {
            if (isCorrect) {
                occ_menu_start.gotoAndStop('success');

                this.throttle(`timer-${name}-process`, () => {
                    this.callRound();
                }, (delay_sequence));
            } else {
                this.$canStartRound = true;

                btn_start.visible = true;

                occ_menu_start.gotoAndStop('error');

                this.state.high_score = (this.state.score > this.state.high_score) ? this.state.score : this.state.high_score;
            }

            this.state.score = isCorrect ? (this.state.score + 1) : 0;

            this.revampDifficulty(isCorrect);

            this.updateScore();
        });
    };

    /**
     *
     * @param event
     */
    process = (event) => {
        const
            {delay_process} = this.props,
            {round_answers, player_answers} = this.state,
            _this_current = this.get(),
            index = (player_answers.length - 1);

        let {name} = event.target,
            color = name.replace('btn_genius_', '');

        if (!this.$waitingPlayerAnswer) {
            return;
        }

        this.state.player_answers.push(color);

        _this_current[name].visible = false;

        this.throttle(`timer-${color}-enabled`, () => {
            _this_current[name].visible = true;
        }, (delay_process));

        if ((player_answers[index] !== round_answers[index]) || (player_answers.length === round_answers.length)) {
            this.processAnswers();
        }
    };

    /**
     *
     */
    displaySequence = () => {
        const
            {name, delay_sequence} = this.props,
            {round_answers, interval_decrease} = this.state,
            _this_current = this.get(),
            {occ_menu_start} = _this_current;

        this.recursiveSyncMap(round_answers, 0, round_answers.length, (item, next) => {
            this.throttle(`timer-${name}-enabled`, () => {
                _this_current[`btn_${name}_${item}`].visible = false;

                this.throttle(`timer-${name}-disabled`, () => {
                    _this_current[`btn_${name}_${item}`].visible = true;

                    next(true);
                }, (delay_sequence - interval_decrease));
            }, (delay_sequence - interval_decrease));
        }, () => {
            this.$waitingPlayerAnswer = true;

            occ_menu_start.gotoAndStop('check');

            this.toggleGenius();
        });
    };

    /**
     *
     * @param callback
     */
    count = (callback) => {
        const
            {delay_sequence, time_out} = this.props,
            {occ_count_down} = this.get();

        this.countDown(delay_sequence, time_out, (time) => {
            occ_count_down.text = time;
        }, () => {
            this.state.high_score = ((this.state.score > this.state.high_score) ? this.state.score : this.state.high_score);
            (typeof (callback) === 'function') && callback({high_score: this.state.high_score.toString()});
        });
    };

    /**
     *
     */
    start = () => {
        if (this.$canStartRound) {
            this.$canStartRound = false;
            this.callRound();
            this.count(this.inFunction(this.$objectResponse, 'always') ? this.$objectResponse.always : this._void);
        }
    };

    /**
     *
     */
    light = (event) => {
        let {name} = event.target;
        console.log(name);
    };

    /**
     *
     */
    create = () => {
        const
            {name, btn_genius, text_font, text_font_size} = this.props,
            _this_current = this.get(),
            {btn_start, occ_score, occ_high_score, occ_count_down} = _this_current;

        occ_count_down.font = `${text_font_size}px '${text_font}'`;
        occ_score.font = `${text_font_size}px '${text_font}'`;
        occ_high_score.font = `${text_font_size}px '${text_font}'`;

        this.clear(true);

        this.$objectResponse.resolve = () => {
            this.clear(true);
        };

        /**
         *
         *  Add event in the button for start.
         */
        this.removeListener('click', `btn-${name}-start`, this.start, () => {
            this.addListener(btn_start, 'click', `btn-${name}-start`, this.start);
        });

        // /**
        //  *
        //  *  Add event in the button for light.
        //  */
        // this.removeListener('click', `btn-${name}-light`, this.light, () => {
        //     this.addListener(btn_action, 'click', `btn-${name}-light`, this.light);
        // });

        this.recursiveSyncMap(btn_genius, 0, btn_genius.length, (item, next) => {
            this.removeListener('click', `btn-${name}-${item}`, this.process, () => {
                this.addListener(_this_current[`btn_${name}_${item}`], 'click', `btn-${name}-${item}`, this.process);
                next(true);
            });
        });
    };
}

/**
 *
 * @type {{name: Requireable<string>, current_name: Requireable<string>}}
 */
const propTypes = {
    name: PropTypes.string,
    default_frame: PropTypes.string,
    current_name: PropTypes.string,
    library: PropTypes.string,
    btn_genius: PropTypes.array,
    delay_sequence: PropTypes.number,
    delay_process: PropTypes.number,
    time_out: PropTypes.number,
    decrease: PropTypes.number,
    decrease_limit: PropTypes.number,
    text_font: PropTypes.string,
    text_font_size: PropTypes.number,
};

/**
 *
 * @type {{default: {name: string, current_name: null}}}
 */
const config = {
    default: {
        name: 'genius',
        default_frame: 'frame00',
        current_name: null,
        library: null,
        btn_genius: ['yellow', 'blue', 'red', 'green'],
        delay_sequence: 1000,
        delay_process: 100,
        time_out: 90,
        decrease: 10,
        decrease_limit: 800,
        text_font: 'MontserratMedium',
        text_font_size: 28
    }
};

/**
 *
 * @param _this
 * @returns {function(*): GamesGenius}
 */
export default (_this) => (props) => {
    const defaultProps = {...config.default, ...props};
    PropTypes.checkPropTypes(propTypes, defaultProps, 'prop', 'GamesGenius');
    return (new GamesGenius(defaultProps, _this));
};